import React, { memo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { makeStyles } from '@material-ui/core/styles';
import { Divider } from '@material-ui/core';
import { useRouter } from 'next/router';
import { get, isEmpty } from 'lodash';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import { colors, fontSize, maskTextStyles } from '../../constants/styleConstants';
import PerformanceCastContent from '../PerformanceCastContent';
import ViewDetailsLink from '../ViewDetailsLink';
import PerformanceChips from '../PerformanceChips';
import ProductionDatesContent from '../ProductionDatesContent';
import {
  getGroupedProductionsDatesWithinRange,
  getGroupedProductionsDateForTheCard,
  getDynamicRoutePropsForNewTab,
  getRouteWithSlug,
  getPerformanceDate,
  getCountryCity,
  isFutureDatesExist,
  isPastDate,
} from '../../utils/common';
import LinkText from '../LinkText';
import { useTranslation } from '../../src/i18n';
import { TP } from '../../constants/consts';
import { route } from '../../constants/routes';
import LoginDialog from '../LoginDialog';
import NextLink from '../NextLink';
import Title from '../Title';

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
  },
  viewlink: {
    padding: '13px 0',
  },
  divider: {
    backgroundColor: colors.contentDivider,
    margin: '0 -12px',
  },
  types: {
    display: 'flex',
    marginTop: 12,
    flexWrap: 'wrap',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      marginTop: 10,
    },
  },
  subtitle: {
    fontSize: fontSize.medium,
    color: colors.grey3,
    lineHeight: 1.57,
    fontStyle: 'italic',
    [theme.breakpoints.up('sm')]: {
      marginLeft: 'auto',
    },
  },
  venueSection: {
    display: 'flex',
    flexDirection: 'column',
  },
  dates: {
    display: 'inline-flex',
    flexWrap: 'wrap',
    wordBreak: 'break-all',
    alignItems: 'center',
  },
  venue: {
    fontSize: fontSize.medium,
    color: colors.blueChip,
    cursor: 'pointer',
  },
  link: {
    fontSize: 12,
    color: colors.blueChip,
    '&:hover': {
      color: colors.blueChip,
    },
  },
  availability: {
    fontSize: 14,
    color: colors.grey1,
  },
  icons: {
    width: 15,
    height: 15,
    verticalAlign: 'text-top',
    color: colors.grey1,
  },
  maskText: {
    textShadow: '0 0 5px rgb(0, 103, 197, 0.4)',
    ...maskTextStyles,
  },
  blurIcon: {
    filter: 'blur(3px)',
  },
  disabled: {
    cursor: 'not-allowed',
    color: colors.grey1,
    opacity: 0.5,
  },
  liveStream: {
    display: 'none',
    justifyContent: 'flex-end',
  },
  alignItems: 'center',
  cancelledTitle: {
    textDecoration: 'line-through',
    opacity: '0.5',
  },
  row1: {
    paddingBottom: 10,
  },
}));

const VenueContent = ({ venue, onVenueClick }) => {
  const classes = useStyles();
  const linkProps = {
    href: `${route.VENUES}/[country]/[id]/[[...action]]`,
    as: getRouteWithSlug(`${route.VENUES}/${get(venue, 'country.slug')}`, venue),
    target: '_blank',
  };

  return venue.id === -1 ? (
    <div className={classnames(classes.venue, classes.maskText)} onClick={() => onVenueClick()}>
      <LocationOnIcon className={classnames(classes.icons, classes.blurIcon)} />
      <span style={{ marginRight: 16 }}>
        {venue?.name} , {getCountryCity(venue)}
      </span>
    </div>
  ) : (
    <NextLink {...linkProps} className={classes.venue}>
      <LocationOnIcon className={classes.icons} />
      <span style={{ marginRight: 16 }}>
        {venue?.name}, {getCountryCity(venue)}
      </span>
    </NextLink>
  );
};

const ProductionAccordionDescription = ({
  production,
  range,
  viewType = 'production',
  isLoggedIn,
  cancelled,
  hideViewPerformance,
}) => {
  let showTickets = false;
  const classes = useStyles({ cancelled });
  const { t } = useTranslation();
  const [isLoginDialogOpen, setLoginDialog] = React.useState(false);
  const router = useRouter();

  const casts = get(production, 'casts', []);
  const crew = get(production, 'crew', []);
  const other = get(production, 'other', []);
  const subTitles = get(production, 'subtitles', [])
    .map(subtitle => get(subtitle, 'name'))
    .join(', ');

  const languages = get(production, 'languages', [])
    .map(subtitle => get(subtitle, 'name'))
    .join(', ');

  const subTitleSeperator = subTitles && languages ? ';' : '';
  const venues = get(production, 'venues', []);

  // to get the coproductions from companies
  const coProductions = production?.companies?.filter(c => c?.is_partner && c?.id !== production?.company.id);

  const checkPermissions = () => {
    if (isLoggedIn) {
      router.push(route.CASTING_TOOL);
    } else if (!isLoggedIn) {
      setLoginDialog(true);
    }
  };

  // to get the subtitles content
  const getSubtitles = (
    <span className={classes.subtitle}>
      {languages && (
        <span>
          {t(`${TP}.PERFORMED_IN`)} {languages}
        </span>
      )}
      {subTitleSeperator}
      {subTitles && (
        <span>
          &nbsp; {t(`${TP}.SUBTITLED_IN`)} {subTitles}
        </span>
      )}
    </span>
  );

  // prodution details link
  const productionDetailsLinkProps =
    viewType === 'production'
      ? getDynamicRoutePropsForNewTab(route.PRODUCTIONS, production)
      : getDynamicRoutePropsForNewTab(route.PRODUCTIONS, production.production);

  // to get the performances dates content
  const performanceDates = (
    <div className={classes.dates}>
      <Title
        size="medium"
        weight="regular"
        style={{
          opacity: isPastDate(production?.date) ? 0.5 : 1,
        }}
        className={classnames(cancelled && classes.cancelledTitle)}
      >
        {getPerformanceDate(production?.date, 'dddd DD MMM, YYYY')} {production?.time && `| ${production?.time}`}
      </Title>
      {!production?.isArchived && !hideViewPerformance && (
        <NextLink {...productionDetailsLinkProps} className={classes.venue}>
          &nbsp; {t('operabase.general.FN_MORE_DATES')}
        </NextLink>
      )}
    </div>
  );

  const grouppedDates = range
    ? getGroupedProductionsDatesWithinRange(production, range)
    : getGroupedProductionsDateForTheCard(production, false);

  const lengthOfGroupedDates = Object.keys(grouppedDates).length;

  if (viewType === 'production') {
    showTickets = !cancelled && lengthOfGroupedDates > 0 && production?.boxOfficeWebsite !== null;
  } else {
    // check if the performance date is in future
    showTickets = !cancelled && isFutureDatesExist([production]) && production?.boxOfficeWebsite !== null;
  }

  const getDatesSection = venue => {
    let defaultGroupedDates = grouppedDates;
    if (venue && viewType === 'production') {
      const dates = production?.dates?.filter(date => date?.venue?.id === venue?.id);
      const datesObj = {
        dates,
      };
      defaultGroupedDates = range
        ? getGroupedProductionsDatesWithinRange(datesObj, range)
        : getGroupedProductionsDateForTheCard(datesObj, false);
    }
    return (
      <>
        {viewType === 'production' ? (
          <ProductionDatesContent
            className={classes.dates}
            cancelled={cancelled}
            formattedDates={defaultGroupedDates}
          />
        ) : (
          performanceDates
        )}
        {showTickets && (
          <LinkText
            className={classnames(classes.link, cancelled && classes.disabled)}
            style={{ float: 'right' }}
            text={t('operabase.general.FN_TICKETS')}
            href={production?.boxOfficeWebsite}
            target="_blank"
          />
        )}
      </>
    );
  };

  // to get the dates section content
  const getVenueSection = (
    <div className={classes.venueSection}>
      {venues?.length > 0 ? (
        venues.map(venue => (
          <div className={classes.row1}>
            {!isEmpty(venue) && <VenueContent venue={venue} onVenueClick={venue.id === -1 && checkPermissions} />}
            {getDatesSection(venue)}
          </div>
        ))
      ) : (
        <>{getDatesSection()}</>
      )}
      <div className={classes.liveStream}>
        <span className={classes.availability}>Available online until Friday, 31 July 2020 17:00 PST</span>
        <LinkText
          className={classnames(classes.link, cancelled && classes.disabled)}
          text={t('operabase.general.FN_FULL_PRODUCTION_VIDEO')}
          href="#"
          target="_blank"
        />
      </div>
    </div>
  );
  return (
    <div className={classes.root}>
      <Divider className={classes.divider} />
      <div className={classes.types}>
        <PerformanceChips
          type="big"
          festivals={production?.festivals}
          coProductions={coProductions}
          isCoProduced={coProductions?.length > 0}
          performanceTypes={production?.performanceTypes}
          checkPermissions={checkPermissions}
        />
        {getSubtitles}
      </div>
      {getVenueSection}
      <PerformanceCastContent cast={casts} crew={crew} other={other} />
      <Divider className={classes.divider} />
      {!hideViewPerformance && (
        <ViewDetailsLink
          className={classes.viewlink}
          linkText={
            viewType === 'production'
              ? t('operabase.general.FN_VIEW_PRODUCTION_DETAILS')
              : t('operabase.general.m_ARTICLES_PERF_DETAILS')
          }
          linkProps={productionDetailsLinkProps}
          size="medium"
          weight="bold"
          // onClick={production?.isArchived ? checkPermissions : undefined}
        />
      )}

      <LoginDialog
        isOpen={isLoginDialogOpen}
        url={productionDetailsLinkProps}
        onClose={() => setLoginDialog(false)}
        message={t(`${TP}.ARCHIVAL_ACCESS_MESSAGE`)}
      />
    </div>
  );
};

ProductionAccordionDescription.propTypes = {
  production: PropTypes.object,
  viewType: PropTypes.string,
  cancelled: PropTypes.bool,
  range: PropTypes.string,
  isLoggedIn: PropTypes.bool,
  hideViewPerformance: PropTypes.bool,
};

export default memo(ProductionAccordionDescription);
